import { useState, useEffect, useRef } from 'react'
import { slugifyHotelName } from '../utils/hotelUrl'
import { FiMapPin, FiArrowRight, FiChevronLeft, FiChevronRight } from 'react-icons/fi'
import { optimizeDealImage } from '../utils/imageOptimization'
import { getRatingLabel } from '../utils/ratingLabels'
import './RegionalDeals.css'

// Firebase Function endpoint for regional deals
const REGIONAL_DEALS_API = 'https://us-central1-voyabay-hotels.cloudfunctions.net/getAllRegionalDeals'

// Region configuration with display names
const REGIONS = {
  northAmerica: { name: 'North America', shortName: 'North America' },
  europe: { name: 'Europe', shortName: 'Europe' },
  asia: { name: 'Asia', shortName: 'Asia' },
  latinAmerica: { name: 'Latin America', shortName: 'Latin America' },
  africa: { name: 'Africa', shortName: 'Africa' },
  oceania: { name: 'Oceania', shortName: 'Oceania' },
}

const REGION_KEYS = ['northAmerica', 'europe', 'asia', 'latinAmerica', 'africa', 'oceania']

function RegionalDeals() {
  const [dealsByRegion, setDealsByRegion] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeRegion, setActiveRegion] = useState('northAmerica')
  
  // Carousel refs and state
  const carouselRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)
  
  // Drag state using refs for synchronous access
  const isDragging = useRef(false)
  const startX = useRef(0)
  const scrollStart = useRef(0)
  const didDrag = useRef(false)

  useEffect(() => {
    fetchAllDeals()
  }, [])

  // Reset scroll position when active region changes
  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = 0
      checkScrollButtons()
    }
  }, [activeRegion])

  const fetchAllDeals = async () => {
    try {
      setLoading(true)
      const response = await fetch(REGIONAL_DEALS_API, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' }
      })
      if (!response.ok) throw new Error('Failed to fetch deals')
      const data = await response.json()
      
      console.log('[RegionalDeals] API response:', data)
      
      if (data.deals) {
        setDealsByRegion(data.deals)
        // Set first region with deals as active
        const firstWithDeals = REGION_KEYS.find(key => data.deals[key]?.length > 0)
        if (firstWithDeals) {
          setActiveRegion(firstWithDeals)
        }
      }
    } catch (err) {
      console.error('Error fetching regional deals:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const checkScrollButtons = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current
      setCanScrollLeft(scrollLeft > 5)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5)
    }
  }

  useEffect(() => {
    checkScrollButtons()
    const carousel = carouselRef.current
    if (carousel) {
      carousel.addEventListener('scroll', checkScrollButtons)
      window.addEventListener('resize', checkScrollButtons)
      return () => {
        carousel.removeEventListener('scroll', checkScrollButtons)
        window.removeEventListener('resize', checkScrollButtons)
      }
    }
  }, [dealsByRegion, activeRegion])

  const scroll = (direction) => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.querySelector('.deal-card')?.offsetWidth || 280
      const gap = 16
      const scrollAmount = (cardWidth + gap) * 4
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  // Drag-to-scroll handlers
  const handleMouseDown = (e) => {
    isDragging.current = true
    didDrag.current = false
    startX.current = e.pageX
    scrollStart.current = carouselRef.current?.scrollLeft || 0
    if (carouselRef.current) {
      carouselRef.current.style.scrollSnapType = 'none'
      carouselRef.current.style.scrollBehavior = 'auto'
    }
  }

  const handleMouseMove = (e) => {
    if (!isDragging.current || !carouselRef.current) return
    const dx = e.pageX - startX.current
    if (Math.abs(dx) > 5) {
      didDrag.current = true
      carouselRef.current.scrollLeft = scrollStart.current - dx
    }
  }

  const handleMouseUp = () => {
    isDragging.current = false
    if (carouselRef.current) {
      carouselRef.current.style.scrollSnapType = 'x mandatory'
      carouselRef.current.style.scrollBehavior = ''
    }
  }

  const handleClick = (e) => {
    if (didDrag.current) {
      e.preventDefault()
      didDrag.current = false
    }
  }

  // Build deep link to hotel page with dates
  const buildHotelLink = (deal) => {
    const params = new URLSearchParams({
      language: 'en',
      currency: deal.currency || 'USD',
      source: 'direct',
      checkin: deal.checkIn || '',
      checkout: deal.checkOut || '',
      rooms: '1',
      adults: '2',
    })

    return `/hotel/${deal.hotelId}/${slugifyHotelName(deal.hotelName)}?${params.toString()}`
  }

  // Format date as local
  const formatDateLocal = (dateStr) => {
    if (!dateStr) return ''
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // Calculate number of nights
  const getNights = (checkIn, checkOut) => {
    if (!checkIn || !checkOut) return 1
    const [y1, m1, d1] = checkIn.split('-').map(Number)
    const [y2, m2, d2] = checkOut.split('-').map(Number)
    const start = new Date(y1, m1 - 1, d1)
    const end = new Date(y2, m2 - 1, d2)
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24))
    return nights > 0 ? nights : 1
  }

  // Format price with currency
  const formatPrice = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  // Get regions that have deals
  const regionsWithDeals = REGION_KEYS.filter(key => dealsByRegion[key]?.length > 0)
  const activeDeals = dealsByRegion[activeRegion] || []

  if (loading) {
    return (
      <section className="regional-deals">
        <div className="container">
          <div className="regional-deals-header">
            <h2>Featured hotel deals</h2>
            <p className="header-subtitle">Updated daily with the best savings worldwide</p>
          </div>
          
          {/* Loading skeleton tabs */}
          <div className="region-tabs">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="region-tab skeleton-tab" />
            ))}
          </div>
          
          {/* Loading skeleton cards */}
          <div className="carousel-container">
            <div className="carousel-track">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="deal-card skeleton">
                  <div className="deal-image-skeleton" />
                  <div className="deal-info-skeleton">
                    <div className="skeleton-line title" />
                    <div className="skeleton-line location" />
                    <div className="skeleton-line price" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    )
  }

  if (error || regionsWithDeals.length === 0) {
    return null
  }

  return (
    <section className="regional-deals">
      <div className="container">
        <div className="regional-deals-header">
          <h2>Featured hotel deals</h2>
          <p className="header-subtitle">Updated daily with the best savings worldwide</p>
        </div>

        {/* Region Tabs */}
        <div className="region-tabs-wrapper">
          <div className="region-tabs">
            {regionsWithDeals.map(regionKey => (
              <button
                key={regionKey}
                className={`region-tab ${activeRegion === regionKey ? 'active' : ''}`}
                onClick={() => setActiveRegion(regionKey)}
              >
                {REGIONS[regionKey].shortName}
              </button>
            ))}
          </div>
        </div>

        {/* Single Carousel for Active Region */}
        <div className="carousel-container">
          {/* Left Arrow */}
          <button 
            className={`carousel-arrow carousel-arrow-left ${!canScrollLeft ? 'hidden' : ''}`}
            onClick={() => scroll('left')}
            aria-label="Scroll left"
          >
            <FiChevronLeft size={24} />
          </button>

          {/* Carousel Track */}
          <div 
            className="carousel-track"
            ref={carouselRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            {activeDeals.map((deal) => (
              <a
                key={deal.id || deal.hotelId}
                href={buildHotelLink(deal)}
                className="deal-card"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClick}
                draggable={false}
              >
                <div className="deal-image">
                  <img 
                    src={optimizeDealImage(deal.thumbnail) || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80'} 
                    alt={deal.hotelName}
                    loading="lazy"
                    width="313"
                    height="209"
                  />
                  <div className="deal-badge">
                    <span className="savings-percent">Save {Math.round(deal.savingsPercent)}%</span>
                  </div>
                </div>
                
                <div className="deal-info">
                  <div className="deal-info-row">
                    <div className="deal-info-left">
                      <div className="deal-location">
                        <FiMapPin size={14} />
                        <span>{deal.destination}</span>
                      </div>
                      
                      <h4 className="deal-title">{deal.hotelName}</h4>
                      
                      <div className="deal-rating">
                        <span className="rating-badge">{deal.rating?.toFixed(1) || '8.0'}</span>
                        <span className="rating-label">{getRatingLabel(deal.rating)}</span>
                      </div>
                    </div>

                    <div className="deal-info-right">
                      <div className="deal-pricing">
                        {(() => {
                          const nights = getNights(deal.checkIn, deal.checkOut)
                          // Use same math as SearchResultsPage: (retailRate - commission - feesIncluded) / nights
                          const commission = deal.commission || 0
                          const feesIncluded = deal.feesIncluded || 0
                          const nightlyRate = (deal.retailRate - commission - feesIncluded) / nights
                          const nightlyOriginal = deal.suggestedSellingPrice / nights
                          return (
                            <>
                              <div className="price-comparison">
                                <span className="original-price">{formatPrice(nightlyOriginal, deal.currency)}</span>
                                <span className="current-price">{formatPrice(nightlyRate, deal.currency)}</span>
                              </div>
                              <span className="price-period">per night</span>
                            </>
                          )
                        })()}
                      </div>

                      <div className="deal-dates">
                        {deal.checkIn && deal.checkOut && (
                          <span>{formatDateLocal(deal.checkIn)} - {formatDateLocal(deal.checkOut)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="deal-cta">
                  <span>View Deal</span>
                  <FiArrowRight size={16} />
                </div>
              </a>
            ))}
          </div>

          {/* Right Arrow */}
          <button 
            className={`carousel-arrow carousel-arrow-right ${!canScrollRight ? 'hidden' : ''}`}
            onClick={() => scroll('right')}
            aria-label="Scroll right"
          >
            <FiChevronRight size={24} />
          </button>
        </div>
      </div>
    </section>
  )
}

export default RegionalDeals
